import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { trackContactClick } from "@/lib/analytics";
import LanguageToggle from "./LanguageToggle";

const WHATSAPP_NUMBER = "963992367582";

const PublicNavbar = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const localizedPath = useLocalizedPath();
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("common.whatsapp_initial_inquiry"),
    )}`;

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinks: { name: string; path: string }[] = [
        { name: t("nav.home"), path: "/" },
        { name: t("nav.portfolio"), path: "/portfolio" },
        { name: t("nav.blog"), path: "/blog" },
        { name: t("nav.videos"), path: "/videos" },
        { name: t("nav.about"), path: "/about" },
        { name: t("nav.contact"), path: "/contact" },
    ];

    const isActive = (path: string) => {
        const localized = localizedPath(path);
        return location.pathname === localized;
    };

    return (
        <nav
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
                "pt-[env(safe-area-inset-top)]",
                // Mobile: always compact, slim. Desktop: large hero nav that shrinks on scroll.
                "py-3 lg:py-8",
                isScrolled
                    ? "bg-background/70 backdrop-blur-xl border-b border-white/5 lg:py-4 shadow-lg"
                    : "bg-background/40 lg:bg-transparent backdrop-blur-md lg:backdrop-blur-none",
            )}
            dir={dir}
        >
            <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
                {/* Logo */}
                <Link to={localizedPath("/")} className="flex items-center gap-2 group">
                    <img
                        src="/assets/logo/logo-transparent.png"
                        alt={t("common.brand")}
                        className="h-9 lg:h-11 w-auto transition-transform duration-300 group-hover:scale-105"
                        loading="eager"
                        decoding="async"
                    />
                </Link>

                {/* Desktop Links — brutalist mono with neon underline on active.
                    Tighter gap at lg (1024–1279) so Arabic labels fit without
                    wrap; full gap at xl+ where there's headroom. */}
                <div className="hidden lg:flex items-center lg:gap-5 xl:gap-7">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={localizedPath(link.path)}
                            className={cn(
                                "font-mono-brutal text-[11px] transition-colors duration-200 relative py-1",
                                "hover:text-white",
                                isActive(link.path)
                                    ? "text-white"
                                    : "text-white/55"
                            )}
                        >
                            {link.name}
                            {isActive(link.path) && (
                                <span
                                    aria-hidden
                                    className="absolute -bottom-1 inset-x-0 h-px"
                                    style={{ background: "var(--gradient-accent-v2)" }}
                                />
                            )}
                        </Link>
                    ))}
                </div>

                {/* Action Buttons. The Login link is hidden at lg (1024–1279)
                    and reappears at xl+ — at lg width the 6 nav links + login
                    + lang toggle + WhatsApp button overflow in Arabic. Login
                    stays reachable through the MobileTabBar's "More" sheet on
                    smaller screens, and via the dedicated /login route. */}
                <div className="hidden lg:flex items-center gap-4">
                    {isAuthenticated ? (
                        <Link to="/dashboard" className="hidden xl:flex">
                            <span className="font-mono-brutal text-[11px] text-white/55 hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                                <LayoutDashboard size={14} />
                                {t("common.dashboard")}
                            </span>
                        </Link>
                    ) : (
                        <Link to="/login" className="hidden xl:flex">
                            <span className="font-mono-brutal text-[11px] text-white/55 hover:text-white transition-colors cursor-pointer">
                                {t("common.login")}
                            </span>
                        </Link>
                    )}
                    <LanguageToggle />
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackContactClick("whatsapp_sy", "navbar")}
                        className="brutal-btn brutal-btn-primary px-5 py-2.5 text-[11px]"
                    >
                        <MessageCircle size={14} />
                        {t("common.whatsapp")}
                    </a>
                </div>

                {/* Mobile: compact language toggle (primary nav lives in MobileTabBar) */}
                <div className="lg:hidden">
                    <LanguageToggle />
                </div>
            </div>
        </nav>
    );
};

export default PublicNavbar;
